
import Vue from 'vue';
import _mapKeys from 'lodash/mapKeys';
import { RunnitMember, TeamMember } from '@run-diffusion/shared';
import { mapState } from 'vuex';
import TableBulkActionsPopover from '@/components/base/TableBulkActionsPopover.vue';
import RunnitBoardMemberRow from '@/views/Runnits/RunnitManageAccessDialog/RunnitBoardMemberRow.vue';

interface CombinedItem {
	id: string
	combinedDate?: Date
	runnitMember?: RunnitMember
	teamMember?: TeamMember
}

export default Vue.extend({
	name: 'RunnitBoardMembersManagement',
	props: {
		runnit: { type: Object, default: null },
		// Who owns the board once saved, when the dialog has a new owner picked. Defaults to the board's.
		ownerUserId: { type: String, default: null },
		// That owner's team member, listed as joining when they are not on the board yet.
		ownerTeamMember: { type: Object, default: null },
		runnitMembers: { type: Array, default: () => [] },
		addedTeamMembers: { type: Array, default: () => [] },
		removedMemberIds: { type: Object, default: () => ({}) },
		loadingRunnitMembers: { type: Boolean, default: false },
	},
	data () {
		const DEFAULT_PAGINATION = {
			sortBy: ['combinedDate'],
			sortDesc: [true],
		};
		return {
			DEFAULT_PAGINATION,
			pagination: DEFAULT_PAGINATION,
			itemsPerPageOptions: [25, 100],
			selectedRows: [],
		};
	},
	computed: {
		...mapState([
			'user',
		]),
		effectiveOwnerUserId (): string | null {
			return this.ownerUserId || this._get(this.runnit, 'userId') || null;
		},
		combinedItems(): CombinedItem[] {
			const members: CombinedItem[] = [];
			const memberDocUserIds: Record<string, true> = {};

			// Add existing runnitMembers (filter out removed ones)
			this.runnitMembers.forEach((runnitMember: RunnitMember) => {
				memberDocUserIds[runnitMember.userId] = true;
				if (!this.removedMemberIds[runnitMember.id]) {
					members.push({
						id: runnitMember.id,
						runnitMember,
						combinedDate: runnitMember.createdAt,
					});
				}
			});

			// Anyone in the board's access map with no runnitMembers doc. `runnit.userIds` is what
			// actually grants access (libraryBoards.ts and RunnitBoardsDashboard.vue both query
			// `userIds.{uid}`), and manageRunnitBoardAccessSettings commits it FIRST, before the
			// member docs, so a partial failure leaves users who can open the board. Listing only
			// member docs made them invisible here: still able to use the board, with no way for
			// an admin to see or remove them. Removing one sends its userId in removedUserIds,
			// which clears the access map whether or not a member doc was ever written.
			const listedUserIds: Record<string, true> = { ...memberDocUserIds };
			Object.keys(this._get(this.runnit, 'userIds') || {}).forEach((userId: string) => {
				if (listedUserIds[userId]) return;
				listedUserIds[userId] = true;
				if (this.removedMemberIds[userId]) return;
				members.push({
					id: userId,
					// No createdAt: there is no member document to have recorded one.
					runnitMember: { id: userId, userId } as RunnitMember,
				});
			});

			// Add new teamMembers to be added (deduplicate against everyone already listed)
			this.addedTeamMembers.forEach((teamMember: TeamMember) => {
				if (!listedUserIds[teamMember.userId]) {
					listedUserIds[teamMember.userId] = true;
					members.push({
						id: teamMember.id,
						teamMember,
						combinedDate: new Date(),
					});
				}
			});

			// The owner is always on a RESTRICTED board, so a newly picked owner who is not on it
			// yet is listed as joining, the way saving adds them.
			const owner: TeamMember | null = this.ownerTeamMember;
			if (owner && owner.userId && owner.userId === this.effectiveOwnerUserId && !listedUserIds[owner.userId]) {
				members.push({
					id: owner.id || owner.userId,
					teamMember: owner,
					combinedDate: new Date(),
				});
			}

			return members;
		},
		headers () {
			return [
				{
					noDownload: true,
					text: 'Email',
					align: 'left',
					class: 'py-0 pl-0 pr-1',
					sortable: false,
				},
				{
					noDownload: true,
					text: 'Added',
					value: 'combinedDate',
					align: 'left',
					class: 'py-0 px-1',
					sortable: true,
				},
				{
					noDownload: true,
					text: '',
					align: 'right',
					class: 'py-0 pr-4',
					sortable: false,
				},
			];
		},
	},
	watch: {
		combinedItems: {
			immediate: true,
			handler (newVal: CombinedItem[], oldVal: CombinedItem[]) {
				// Sync selectedRows with latest data
				if (newVal !== oldVal && this.selectedRows.length > 0) {
					const allMembersMap: Record<string, CombinedItem> = _mapKeys(newVal, 'id');
					this.selectedRows = this.selectedRows.map((member: CombinedItem) => allMembersMap[member.id] || member);
				}
			},
		},
	},
	methods: {
		clearSelectedRows () {
			this.$nextTick(() => {
				// Next tick seems to prevent issue with check boxes getting in a state where they're unselectable after a bulk update and clear selected items operation
				this.selectedRows = [];
			});
		},
		onRemoveItems(items: CombinedItem[]) {
			const existingMemberIds: string[] = [];
			const teamMembersToRemove: TeamMember[] = [];

			items.forEach((item: CombinedItem) => {
				if (item.runnitMember) {
					existingMemberIds.push(item.runnitMember.id);
				}
				if (item.teamMember) {
					teamMembersToRemove.push(item.teamMember);
				}
			});

			if (existingMemberIds.length > 0) {
				this.$emit('remove-existing-members', existingMemberIds);
			}
			if (teamMembersToRemove.length > 0) {
				this.$emit('remove-added-members', teamMembersToRemove);
			}
		},
		onRemoveItemsInBulk() {
			this.onRemoveItems(this.selectedRows);
			this.clearSelectedRows();
		},
	},
	components: {
		TableBulkActionsPopover,
		RunnitBoardMemberRow,
	},
});
